/**
 * ReplayViewProseMirror Component
 *
 * A ProseMirror-based read-only document viewer that displays a ProseMirror document
 * with highlighted changes. Unlike ReplayDocumentProseMirror which receives plain text,
 * this component receives a pre-built ProseMirror document with full formatting preserved.
 *
 * This component is used for rich text replay where ProseMirror steps have been applied
 * to reconstruct the document with formatting (bold, italic, headings, etc.).
 *
 * @example
 * ```tsx
 * const state = pmDocumentBuilder.getStateAtIndex(50);
 *
 * <ReplayViewProseMirror
 *   document={state.doc}
 *   changeRanges={state.changeRanges}
 *   autoScroll={true}
 * />
 * ```
 */

import { useRef, useEffect, useCallback, useMemo } from "react";
import { Editor, type EditorRef, type ProseMirrorNode } from "@markeroo/editor";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import type { Transaction, EditorState } from "@tiptap/pm/state";
import type { PMChangeRange, ReplayEventType } from "../types";

export interface ReplayViewProseMirrorProps {
  /** ProseMirror document node to display */
  document: ProseMirrorNode;
  /** Ranges to highlight in the document (ProseMirror positions) */
  changeRanges: PMChangeRange[];
  /** Auto-scroll to keep changes visible (default: true) */
  autoScroll?: boolean;
  /** Optional className */
  className?: string;
  /** Test ID for the component */
  testId?: string;
  /** Empty state message */
  emptyMessage?: string;
}

// Reading zone boundaries as percentage of viewport (30%-70% zone)
const READING_ZONE_TOP = 0.3;
const READING_ZONE_BOTTOM = 0.3;
// Minimum time between scroll operations (ms)
const MIN_SCROLL_INTERVAL_MS = 80;
// Threshold for using instant vs smooth scroll (% of viewport distance)
const INSTANT_SCROLL_THRESHOLD = 0.6;

// =============================================================================
// PM Change Decoration Plugin (uses ProseMirror positions directly)
// =============================================================================

/**
 * Plugin state interface for PM change decorations.
 */
interface PMChangeDecorationState {
  decorations: DecorationSet;
  changeRanges: PMChangeRange[];
}

const pmChangeDecorationPluginKey = new PluginKey<PMChangeDecorationState>(
  "pmChangeDecoration"
);

/**
 * CSS classes for addition highlights based on event type.
 * Uses brand semantic colors for consistency.
 */
function getAdditionClass(eventType: ReplayEventType | undefined): string {
  if (eventType === "paste") {
    return "bg-semantic-warning-bg rounded-sm px-0.5";
  }
  return "bg-semantic-success-bg rounded-sm px-0.5";
}

/**
 * CSS classes for deletion highlights.
 * Uses brand semantic error color for consistency.
 */
const DELETION_CLASS =
  "bg-semantic-error-bg line-through text-semantic-error rounded-sm px-0.5";

/**
 * Create decorations from PM change ranges.
 * Unlike createChangeDecorations in the editor package, this uses
 * ProseMirror positions directly (no conversion from text positions).
 */
function createPMChangeDecorations(
  changeRanges: PMChangeRange[],
  doc: ProseMirrorNode
): DecorationSet {
  const decorations: Decoration[] = [];

  for (const range of changeRanges) {
    // Validate positions are within document bounds
    // Valid positions are 0 to doc.nodeSize - 1
    const maxPos = doc.nodeSize - 1;
    const from = Math.max(0, Math.min(range.from, maxPos));
    const to = Math.max(0, Math.min(range.to, maxPos));

    if (range.type === "addition") {
      // Skip invalid ranges (from must be < to for inline decorations)
      if (from >= to) continue;

      // Create inline decoration for additions
      decorations.push(
        Decoration.inline(from, to, {
          class: getAdditionClass(range.eventType),
          "data-change-range": "addition",
          "data-event-type": range.eventType ?? "insert",
        })
      );
    } else if (range.type === "deletion") {
      // For deletions, we show a widget at the deletion position
      // If there's text content from marks, we could show it
      // For now, show a marker for formatting removals

      if (range.marks && range.marks.length > 0) {
        // This is a formatting removal (RemoveMarkStep)
        // Highlight the affected range instead of showing a widget
        decorations.push(
          Decoration.inline(from, to, {
            class: DELETION_CLASS,
            "data-change-range": "deletion",
            "data-event-type": range.eventType ?? "delete",
          })
        );
      } else {
        // For text deletions, create a widget showing the actual deleted text
        decorations.push(
          Decoration.widget(
            from,
            () => {
              const span = document.createElement("span");
              span.setAttribute("data-change-range", "deletion");
              span.setAttribute("data-event-type", range.eventType ?? "delete");
              span.className = DELETION_CLASS;

              // Display actual deleted text if available, otherwise fallback to ▸
              const deletedText = range.deletedText ?? "";

              // Truncate long deletions for readability
              const MAX_DISPLAY_LENGTH = 100;
              let displayText = deletedText.length > MAX_DISPLAY_LENGTH
                ? deletedText.substring(0, MAX_DISPLAY_LENGTH) + "…"
                : deletedText;

              // Replace newlines with spaces for inline display
              displayText = displayText.replace(/\n/g, " ");

              // Use fallback if no text or only whitespace
              span.textContent = displayText.trim() || "▸";

              // Accessibility label (also truncated for very long text)
              const MAX_ARIA_LENGTH = 200;
              const ariaText = deletedText.length > MAX_ARIA_LENGTH
                ? deletedText.substring(0, MAX_ARIA_LENGTH) + "…"
                : deletedText;
              const ariaLabel = ariaText.trim()
                ? `Deleted text: ${ariaText}`
                : "Text was deleted here";
              span.setAttribute("aria-label", ariaLabel);

              return span;
            },
            { side: 0 }
          )
        );
      }
    }
  }

  return DecorationSet.create(doc, decorations);
}

/**
 * Set PM change ranges on a transaction.
 */
function setPMChangeRanges(
  tr: Transaction,
  changeRanges: PMChangeRange[]
): void {
  tr.setMeta(pmChangeDecorationPluginKey, changeRanges);
}

/**
 * Create a PM change decoration plugin instance.
 */
function createPMChangeDecorationPlugin(
  initialRanges: PMChangeRange[] = []
): Plugin<PMChangeDecorationState> {
  return new Plugin<PMChangeDecorationState>({
    key: pmChangeDecorationPluginKey,

    state: {
      init(_, state) {
        return {
          decorations: createPMChangeDecorations(initialRanges, state.doc),
          changeRanges: initialRanges,
        };
      },

      apply(tr, value, _oldState, newState) {
        // Check for new change ranges in transaction metadata
        const newRanges = tr.getMeta(pmChangeDecorationPluginKey) as
          | PMChangeRange[]
          | undefined;

        if (newRanges !== undefined) {
          // New ranges provided - rebuild decorations
          return {
            decorations: createPMChangeDecorations(newRanges, newState.doc),
            changeRanges: newRanges,
          };
        }

        if (tr.docChanged) {
          // Document changed - map existing decorations
          return {
            decorations: value.decorations.map(tr.mapping, newState.doc),
            changeRanges: value.changeRanges,
          };
        }

        // No changes - return existing state
        return value;
      },
    },

    props: {
      decorations(state: EditorState) {
        const pluginState = pmChangeDecorationPluginKey.getState(state);
        return pluginState?.decorations ?? DecorationSet.empty;
      },
    },
  });
}

// =============================================================================
// Component
// =============================================================================

export function ReplayViewProseMirror({
  document,
  changeRanges,
  autoScroll = true,
  className = "",
  testId = "replay-view-prosemirror",
  emptyMessage = "No content yet",
}: ReplayViewProseMirrorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<EditorRef>(null);
  const lastScrollTimeRef = useRef<number>(0);
  const pendingScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // Create the PM change decoration plugin once
  const pmChangeDecorationPlugin = useMemo(
    () => createPMChangeDecorationPlugin(),
    []
  );

  // Update the editor content and decorations when document or changeRanges change
  useEffect(() => {
    const view = editorRef.current?.getView();
    if (!view) return;

    let cancelled = false;

    // Use changeRanges directly - merging is handled in computeRangeChangeRangesFromSteps
    const mergedChangeRanges = changeRanges;

    // Update document content
    editorRef.current?.setContent(document);

    // Capture merged changeRanges in closure
    const currentChangeRanges = mergedChangeRanges;

    // Update change decorations via plugin metadata
    const frameId = requestAnimationFrame(() => {
      if (cancelled) return;

      const currentView = editorRef.current?.getView();
      if (!currentView) return;

      const tr = currentView.state.tr;
      setPMChangeRanges(tr, currentChangeRanges);
      currentView.dispatch(tr);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
    };
  }, [document, changeRanges]);

  /**
   * Smart auto-scroll that keeps current change visible in a "reading zone"
   * (30-70% of viewport) for a better reading experience during playback.
   */
  const scrollToChangeInReadingZone = useCallback(() => {
    if (!autoScroll) return;

    // Clear any pending scroll
    if (pendingScrollTimeoutRef.current) {
      clearTimeout(pendingScrollTimeoutRef.current);
      pendingScrollTimeoutRef.current = null;
    }

    const changeElement = containerRef.current?.querySelector<HTMLElement>(
      '[data-change-range="addition"], [data-change-range="deletion"]'
    );

    if (!changeElement || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const changeRect = changeElement.getBoundingClientRect();

    // Skip if container has no height (not rendered yet)
    if (containerRect.height === 0 || changeRect.height === 0) return;

    // Calculate reading zone thresholds
    const topThreshold =
      containerRect.top + containerRect.height * READING_ZONE_TOP;
    const bottomThreshold =
      containerRect.bottom - containerRect.height * READING_ZONE_BOTTOM;

    // Check if change is within the reading zone
    const isInReadingZone =
      changeRect.top >= topThreshold && changeRect.bottom <= bottomThreshold;

    if (isInReadingZone) {
      // Already visible in reading zone - no scroll needed
      return;
    }

    // Calculate distance from center
    const changeCenter = changeRect.top + changeRect.height / 2;
    const containerCenter = containerRect.top + containerRect.height / 2;
    const distanceFromCenter = Math.abs(changeCenter - containerCenter);

    // Skip if distance is minimal (within 15% of container height)
    const minScrollDistance = containerRect.height * 0.15;
    if (distanceFromCenter < minScrollDistance) return;

    // Rate limiting - check if enough time has passed since last scroll
    const now = performance.now();
    const timeSinceLastScroll = now - lastScrollTimeRef.current;

    if (
      timeSinceLastScroll < MIN_SCROLL_INTERVAL_MS &&
      lastScrollTimeRef.current !== 0
    ) {
      // Too soon - schedule a delayed scroll instead
      pendingScrollTimeoutRef.current = setTimeout(() => {
        scrollToChangeInReadingZone();
      }, MIN_SCROLL_INTERVAL_MS - timeSinceLastScroll);
      return;
    }

    // Determine scroll behavior based on distance
    const shouldInstantScroll =
      distanceFromCenter > containerRect.height * INSTANT_SCROLL_THRESHOLD;

    changeElement.scrollIntoView({
      behavior: shouldInstantScroll ? "instant" : "smooth",
      block: "center",
      inline: "nearest",
    });

    lastScrollTimeRef.current = performance.now();
  }, [autoScroll]);

  // Scroll to change when changeRanges updates
  useEffect(() => {
    if (changeRanges.length > 0 && autoScroll) {
      let cancelled = false;
      let innerFrameId: number | null = null;

      // Use double requestAnimationFrame for smooth timing after render
      const outerFrameId = requestAnimationFrame(() => {
        if (cancelled) return;
        innerFrameId = requestAnimationFrame(() => {
          if (cancelled) return;
          scrollToChangeInReadingZone();
        });
      });

      return () => {
        cancelled = true;
        cancelAnimationFrame(outerFrameId);
        if (innerFrameId !== null) {
          cancelAnimationFrame(innerFrameId);
        }
      };
    }
  }, [changeRanges, autoScroll, scrollToChangeInReadingZone]);

  // Cleanup pending scroll timeout on unmount
  useEffect(() => {
    return () => {
      if (pendingScrollTimeoutRef.current) {
        clearTimeout(pendingScrollTimeoutRef.current);
      }
    };
  }, []);

  // Empty document state - check if document has no text content
  const isEmpty = document.textContent.length === 0;

  if (isEmpty) {
    return (
      <div
        ref={containerRef}
        className={`flex-1 min-h-0 overflow-y-auto rounded-lg border border-theme-primary bg-surface-primary p-4 ${className}`}
        data-testid={testId}
      >
        <p className="text-center text-theme-muted italic">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`replay-view-prosemirror flex-1 min-h-0 overflow-y-auto rounded-lg border border-theme-primary bg-surface-primary p-4 ${className}`}
      data-testid={testId}
      role="article"
      aria-label="Document content with highlighted changes"
    >
      <Editor
        ref={editorRef}
        initialContent={document}
        editable={false}
        plugins={[pmChangeDecorationPlugin]}
        className="font-serif text-base leading-relaxed text-text-theme-primary"
        testId={`${testId}-editor`}
        ariaLabel="Replay document content"
      />
    </div>
  );
}

export default ReplayViewProseMirror;
